import {
  addCart,
  addOrder,
  back,
  close,
  compareWithAppVersion,
  createOrderSheet,
  deleteCart,
  injectZpayHandler,
  isAndroid,
  isBrowser,
  isIOS,
  login,
  loginWithRedirectUrl,
  logout,
  open,
  productReviewPhotoDetail,
  relatedItemList,
  sendPostMessage,
  setTitle,
  updateCart,
  updateOrder,
  zpayHandler,
} from '@croquiscom-pvt/web2app';
import escape from 'lodash/escape';

import { isZigzag } from '@common/device-manager';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const checkZPayFunctionExists = (browser_window: any) => {
  return Boolean(
    browser_window.zpay ||
      (browser_window.webkit && browser_window.webkit.messageHandlers && browser_window.webkit.messageHandlers.zpay),
  );
};

// Zpay
export enum ZpayPostMessageType {
  REFRESH_SIMPLE_BANK_ACCOUNT_LIST = 'refresh_simple_bank_account_list',
  PURCHASE_BY_SIMPLE_PAY = 'purchase_by_simple_pay',
  ADD_PRODUCT_REVIEW = 'add_product_review',
  PURCHASE_ORDER_SHEET_BY_SIMPLE_PAY = 'purchase_order_sheet_by_simple_pay',
  SET_COUPON_PAYLOADS = 'set_coupon_payloads',
  FORCE_REFRESH_PAGE = 'force_refersh_page',
  INVALIDATION_ADDRESS = 'invalidation_address',
  SELECT_ADDRESS = 'select_address',
  DELETE_ADDRESS = 'delete_address',
  DIRECT_CHECKOUT = 'direct_checkout',
  SELECT_POSTNO_ADDRESS = 'select_postno_address',
  SET_REFUND_BANK_ACCOUNT = 'set_refund_bank_account',
  CANCEL_AND_ADD_TO_CART = 'cancel_and_add_to_cart',
  UPDATE_RECEIVER_ADDRESS = 'update_receiver_address',
  //iframe close
  EXIT_COUPON_DOWNLOAD = 'exit_coupon_download',
  EXIT_COUPON_SELECT = 'exit_coupon_select',
  EXIT_CART_CPLP = 'exit_cart_cplp',
  EXIT_POSTCODE = 'exit_postcode',
  EXIT_ADDRESS_BOOK = 'exit_address_book',
  EXIT_ADDRESS_BOOK_EDIT = 'exit_address_book_edit',
  EXIT_SIMPLE_PAY = 'exit_simple_pay',
  EXIT_REFUND_ACCOUNT = 'exit_refund_account',
  //주문배송페이지 order_item 업데이트
  UPDATE_ORDER_ITEM = 'update_order_item',
  RELOAD_CART_PAGE = 'reload_cart_page',
}

export const getAppScheme = (): string => {
  if (isBrowser() && compareWithAppVersion('5.11.2')) {
    if (isAndroid()) {
      return '';
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const browser_window: any = window;

    if (!browser_window) {
      return 'zigzag://';
    }
    return checkZPayFunctionExists(browser_window) ? 'zigzag://' : '';
  }
  return '';
};

export const overwriteBack = (callback: (back: () => void) => void) => {
  injectZpayHandler();
  zpayHandler('handleBackClick', () => {
    callback(ZpayEvent.back);
    return true;
  });
};

export const overwriteClose = (callback: (close: () => void) => void) => {
  injectZpayHandler();
  zpayHandler('handleCloseClick', () => {
    callback(ZpayEvent.close);
    return true;
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const overwriteOnMessage = (callback: (type: ZpayPostMessageType, message: any, origin?: string) => void) => {
  injectZpayHandler();
  zpayHandler('onMessage', (message, targetOrigin) => {
    if (targetOrigin === '*' || window.location.origin === targetOrigin) {
      const data = JSON.parse(message);
      const type = data.type as ZpayPostMessageType;
      callback(type, data, targetOrigin);
    }
  });
};

const getOrigin = (origin?: string) => (isBrowser() ? origin || window.location.origin : '*');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendPostMessageInWeb = (type: ZpayPostMessageType, data: any = {}, origin?: string) => {
  const targetOrigin = getOrigin(origin);

  if (window.opener !== null) {
    const strMessage = JSON.stringify({ type, ...data });
    window.opener.postMessage(strMessage, targetOrigin);
  }
  if (window.parent) {
    const strMessage = JSON.stringify({ type, ...data });
    window.parent.postMessage(strMessage, targetOrigin);
  }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendPostMessageEvent = (type: ZpayPostMessageType, data: any = {}, origin?: string) => {
  const targetOrigin = getOrigin(origin);

  if (isZigzag()) {
    sendPostMessage({ type, ...data }, targetOrigin);
  } else {
    sendPostMessageInWeb(type, data, targetOrigin);
  }
};

export const ZpayEvent = {
  login,
  loginWithRedirectUrl,
  logout,
  back,
  close,
  createOrderSheet,
  open,
  setTitle,
  addCart,
  deleteCart,
  reloadCart: () => sendPostMessageEvent(ZpayPostMessageType.RELOAD_CART_PAGE),
  updateCart: () => {
    if ((compareWithAppVersion('7.36.0') && isAndroid()) || (compareWithAppVersion('7.35.1') && isIOS())) {
      updateCart();
    }
  },
  addOrder,
  updateOrder,
  productReviewPhotoDetail,
  relatedItemList,
  refreshSimpleBankAccountList: () => sendPostMessageEvent(ZpayPostMessageType.REFRESH_SIMPLE_BANK_ACCOUNT_LIST),
  purchaseBySimplePay: (password: string) =>
    sendPostMessageEvent(ZpayPostMessageType.PURCHASE_BY_SIMPLE_PAY, { password }),
  purchaseOrderSheetBySimplePay: (order_sheet_uuid: string, password: string) =>
    sendPostMessageEvent(ZpayPostMessageType.PURCHASE_ORDER_SHEET_BY_SIMPLE_PAY, { order_sheet_uuid, password }),
  setCouponPayloads: <T extends object>(coupon_payloads: T[]) =>
    sendPostMessageEvent(ZpayPostMessageType.SET_COUPON_PAYLOADS, { coupon_payloads }),
  invalidatonAddress: () => sendPostMessageEvent(ZpayPostMessageType.INVALIDATION_ADDRESS),
  selectAddress: <
    T extends {
      address: string;
      detail_address: string | null;
    },
  >(
    select_address: T,
    from_uuid?: string | null,
  ) =>
    sendPostMessageEvent(ZpayPostMessageType.SELECT_ADDRESS, {
      select_address: {
        ...select_address,
        address: escape(select_address.address),
        detail_address: escape(select_address.detail_address || ''),
      },
      from_uuid,
    }),
  selectPostnoAddress: (post_code: string, address: string, detail_address: string, origin?: string) =>
    sendPostMessageEvent(
      ZpayPostMessageType.SELECT_POSTNO_ADDRESS,
      { post_code, address: escape(address), detail_address: escape(detail_address) },
      origin,
    ),
  deleteAddress: (address_id: string) => sendPostMessageEvent(ZpayPostMessageType.DELETE_ADDRESS, { address_id }),
  directCheckout: () => sendPostMessageEvent(ZpayPostMessageType.DIRECT_CHECKOUT),
  forceRefreshPage: () => sendPostMessageEvent(ZpayPostMessageType.FORCE_REFRESH_PAGE),
  setRefundBankAccount: (account_holder: string, account_number: string, bank_code: string) =>
    sendPostMessageEvent(ZpayPostMessageType.SET_REFUND_BANK_ACCOUNT, { account_holder, account_number, bank_code }),
  cancelAndAddtoCart: () => sendPostMessageEvent(ZpayPostMessageType.CANCEL_AND_ADD_TO_CART),
  exitCouponDownload: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_COUPON_DOWNLOAD),
  exitCouponSelect: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_COUPON_SELECT),
  exitCartCplp: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_CART_CPLP),
  exitPostcode: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_POSTCODE),
  exitAddressBook: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_ADDRESS_BOOK),
  exitAddressBookEdit: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_ADDRESS_BOOK_EDIT),
  exitSimplePay: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_SIMPLE_PAY),
  exitRefundAccount: () => sendPostMessageEvent(ZpayPostMessageType.EXIT_REFUND_ACCOUNT),
  updateReceiverAddress: () => sendPostMessageEvent(ZpayPostMessageType.UPDATE_RECEIVER_ADDRESS),
  updateOrderItem: (order_item_number: string[]) =>
    sendPostMessageEvent(ZpayPostMessageType.UPDATE_ORDER_ITEM, { order_item_number }),
};
